import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  ListItemIcon,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Logout from '@mui/icons-material/Logout';
import LightMode from '@mui/icons-material/LightMode';
import DarkMode from '@mui/icons-material/DarkMode';
import { alpha, useTheme, useColorScheme } from '@mui/material/styles';
import { useAuth } from '../auth/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import Sidebar from './Sidebar';
import GlobalSearchBar from './GlobalSearchBar';
import { ApartmentRounded } from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { GetCurrentTenantDocument } from '@/generated/root-types';

const DRAWER_WIDTH = 256;
const DRAWER_WIDTH_COLLAPSED = 72;
const SIDEBAR_COLLAPSED_KEY = 'reseller_sidebar_collapsed';

export default function MainLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return (
        typeof localStorage !== 'undefined' && localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
      );
    } catch {
      return false;
    }
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const { data: currentTenantData } = useQuery(GetCurrentTenantDocument);
  const resellerLogo = currentTenantData?.currentTenant?.resellerLogo ?? null;
  const { logo, appName } = useBranding();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const muiTheme = useTheme();
  const { mode, systemMode, setMode } = useColorScheme();
  const resolvedMode = mode === 'system' ? systemMode : mode;
  const appBarContrast = muiTheme.palette.primary.contrastText;
  const appBarToggleButtonRoot = {
    color: 'inherit' as const,
    borderColor: alpha(appBarContrast, 0.5),
    py: 0.25,
    px: 0.75,
  };
  const themeToggleSx = { mr: 1, '& .MuiToggleButton-root': appBarToggleButtonRoot };
  const languageToggleSx = {
    mr: 1,
    '& .MuiToggleButton-root': { ...appBarToggleButtonRoot, px: 1 },
  };

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const drawerWidthMd = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const handleMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        color="primary"
        enableColorOnDark
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar disableGutters sx={{ px: 2, gap: 1 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component={RouterLink}
            to="/dashboard"
            aria-label={appName}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              textDecoration: 'none',
              color: 'inherit',
              minHeight: 40,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{
                height: 36,
                width: 'auto',
                maxWidth: { xs: 140, sm: 180, md: 220 },
                objectFit: 'contain',
                objectPosition: 'left center',
                display: 'block',
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 0,
              px: 1,
            }}
          >
            <GlobalSearchBar />
          </Box>
          <ToggleButtonGroup
            value={resolvedMode ?? 'light'}
            exclusive
            onChange={(_, value) => value && setMode(value)}
            size="small"
            sx={themeToggleSx}
          >
            <ToggleButton value="light" aria-label={t('common.themeLight')}>
              <LightMode fontSize="small" />
            </ToggleButton>
            <ToggleButton value="dark" aria-label={t('common.themeDark')}>
              <DarkMode fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={i18n.language}
            exclusive
            onChange={(_, value) => value && i18n.changeLanguage(value)}
            size="small"
            sx={languageToggleSx}
          >
            <ToggleButton value="de" aria-label="Deutsch">
              DE
            </ToggleButton>
            <ToggleButton value="en" aria-label="English">
              EN
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            color="inherit"
            onClick={handleMenu}
            sx={{
              ml: 1,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
              ...(resellerLogo && {
                width: '120px',
                height: '48px',
                borderRadius: '10px',
              }),
            }}
          >
            {resellerLogo ? (
              <img
                src={resellerLogo}
                alt="Reseller Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            )}
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 220,
                borderRadius: 2,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {user?.email}
              </Typography>
            </Box>
            <MenuItem
              onClick={() => {
                navigate('/reseller-organizations');
                handleClose();
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <ApartmentRounded fontSize="small" />
              </ListItemIcon>
              {t('nav.myOrganizations')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
              <ListItemIcon>
                <Logout fontSize="small" color="error" />
              </ListItemIcon>
              {t('Logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidthMd },
          flexShrink: 0,
          transition: (theme) => theme.transitions.create('width'),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthMd,
              mt: '64px',
              transition: (theme) => theme.transitions.create('width'),
              overflowX: 'hidden',
            },
          }}
          open
        >
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
          />
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidthMd}px)` },
          mt: '64px',
          transition: (theme) => theme.transitions.create('width'),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
